import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  SecondaryBtn,
  Textarea,
  WaitingAlert,
  SuccessAlert,
} from '@tmapy/style-guide';

import type { PatchedCeproDocUpdateRequest } from '../../openapi/types';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { useDocsRetrieveResource } from '../../openapi/operations/docsRetrieve';
import { ceproDocsImportDocCreate } from '../../openapi/operations/ceproDocsImportDocCreate';
import { ceproDocsPartialUpdate } from '../../openapi/operations/ceproDocsPartialUpdate';
import { docsListOperation } from '../../openapi/operations/docsList';
import { ceproDocsCreate } from '../../openapi/operations/ceproDocsCreate';
import { ceproDocsImportInterruptPartialUpdate } from '../../openapi/operations/ceproDocsImportInterruptPartialUpdate';
import { ceproDocsCancelPartialUpdate } from '../../openapi/operations/ceproDocsCancelPartialUpdate';
import { ceproDocsSendToDmvsPartialUpdate } from '../../openapi/operations/ceproDocsSendToDmvsPartialUpdate';
import { ceproDocsDestroy } from '../../openapi/operations/ceproDocsDestroy';
import { ceproDocsClosePartialUpdate } from '../../openapi/operations/ceproDocsClosePartialUpdate';
import { ceproDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/ceproDocsCheckDmvsPendingPozadavkyCreate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useSelector,
  useSSE,
  useAttachments,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import {
  ATTACHMENT_TYPES,
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
} from '../constants';
import { loadDoc, updateDoc } from '../actions/docDetail';
import { getFormValues } from '../utils/getformvalues';

import { docFormMessages, docTitleMessages } from './docsMessages';
import { docEditButtonsMsg, docEditMessages, validatorMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { getErrorMessages, ResponseErrorMessage } from './ResponseErrorMessage';
import { DocDownloadFiles } from './DocDownloadFiles';

import './Doc.scss';

export type DocCeproEditContainerProps = {
  docId?: number;
};

export const DocCepro: React.FC<DocCeproEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [cancelQueryState, setCancelQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [sendToDMVSQueryState, setSendToDMVSQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importInterruptQueryState, setImportInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [closeInterruptQueryState, setCloseInterruptQueryState] = useState<QUERY_STATE>(
    QUERY_STATE.EMPTY,
  );
  const [checkDmvsPendingPozadavkyQueryState, setCheckDmvsPendingPozadavkyQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const [validate, setValidate] = useState(false);
  const [importError, setImportError] = useState<any>(null);
  const [queryMessage, setQueryMessage] = useState<{ code: string; message: string }[] | null>(
    null,
  );

  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const stateData = useSelector((state) => state.doc.data);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData
      ? cachedData
      : {
          ...defaultValues,
          state: DOC_STATE.INPROGRESS,
          prejimka_type: PrejimkaTypesEnum.CEPRO_IMPORT,
          flow: FlowEnum.CEPRO_IMPORT,
        };
  }, [resource.data, stateData, docId]);

  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('CeproDocs', 'partial_update', data),
      docsUpdate: auth.hasPermission('CeproDocs', 'update', data),
      docsDestroy: auth.hasPermission('CeproDocs', 'destroy', data),

      docsActionShowLog: auth.hasPermission('CeproDocs', 'action.logs', data),
      docsActionImport: auth.hasPermission('CeproDocs', 'action.import_doc', data),
      docsActionImportInterrupt: auth.hasPermission('CeproDocs', 'action.import_interrupt', data),
      docsActionCancel: auth.hasPermission('CeproDocs', 'action.cancel', data),
      docsActionSendToDMVS: auth.hasPermission('CeproDocs', 'action.send_to_dmvs', data),
      docsActionClose: auth.hasPermission('CeproDocs', 'action.close', data),

      //download section
      docsActionDownloadImportLog: auth.hasPermission('CeproDocs', 'download_import_log', data),
      docsActionDownloadDmvsLog: auth.hasPermission('CeproDocs', 'download_dmvs_log', data),

      // restart dmvs pending
      docsActionCheckDmvsPendingPozadavky: auth.hasPermission(
        'CeproDocs',
        'check_dmvs_pending_pozadavky',
        data,
      ),
    }),
    [auth, data],
  );

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  const dmvsLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_dmvs_log',
    handleShowDownloadLogError,
  );

  const routeDetail = useHistoryAction('ceproDocDetail', { id: docId! });
  const routeList = useHistoryAction('docList', { appType });

  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  // ACTIONS
  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const handleActionStart = useCallback(() => setQueryMessage(null), []);
  const handleActionFail = useCallback(
    (response: CallApiResult<any>) => setQueryMessage(getErrorMessages(response.error.data)),
    [],
  );
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
    onStart: handleActionStart,
    onFail: handleActionFail,
  });

  const handleSaveDocWithValidation = async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems);
    const values = {
      ...formValues,
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: PatchedCeproDocUpdateRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await ceproDocsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            dispatch(updateDoc(response.data));
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await ceproDocsCreate({
            request: values,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleImportInterruptBtnClick = () => {
    doAction(ceproDocsImportInterruptPartialUpdate, setImportInterruptQueryState);
  };
  const handleCancelBtnClick = () => {
    doAction(ceproDocsCancelPartialUpdate, setCancelQueryState);
  };

  const handleCloseBtnClick = () => {
    doAction(ceproDocsClosePartialUpdate, setCloseQueryState);
  };

  const handleSendToDMVSBtnClick = () => {
    doAction(ceproDocsSendToDmvsPartialUpdate, setSendToDMVSQueryState);
  };
  const handleDelete = () => {
    doAction(ceproDocsDestroy, setCloseInterruptQueryState, {
      refetch: false,
      redirectToList: true,
    });
  };

  const handleCheckDmvsPendingPozadavkyBtnClick = () => {
    doAction(ceproDocsCheckDmvsPendingPozadavkyCreate, setCheckDmvsPendingPozadavkyQueryState);
  };

  const handleImportBtnClick = async () => {
    const data = await handleSaveDocWithValidation();
    if (!data) return;

    setImportError(null);
    setImportQueryState(QUERY_STATE.WAITING);
    const importResponse = await ceproDocsImportDocCreate({ query: { id: data.id } });

    if (importResponse.error) {
      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);

      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);
    } else {
      // refresh
      setImportQueryState(QUERY_STATE.SUCCESS);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);
    }
  };

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    validate: validate,
    validator: (value) =>
      !value ? formatMessage(validatorMessages.buildingDescriptionError) : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: !acl.canUpdate,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.description,
  });

  const state = { value: data.state };
  const prejimka_type = { value: data.prejimka_type };
  const formItems = {
    state,
    prejimka_type,
    name,
    description,
  } as const;

  // ATTACHMENTS
  const uploadHelpText = formatMessage.fallback(['document.cepro.uploadHelpText']);
  // omezeni typu dokumentu
  const CEPRO_ATTACHMENT_TYPES = {
    UPDATE_DATA: 'UPDATE_DATA',
    ROZSAH_EDITACE: 'ROZSAH_EDITACE',
    DVOJICE_IDENTIFIKATORU: 'DVOJICE_IDENTIFIKATORU',
  } as typeof ATTACHMENT_TYPES;

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'cepro', {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {acl.docsUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionImport && (
                  <CtaBtn
                    queryState={toBtnQueryState(importQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportBtnClick}
                  >
                    {data.state === DOC_STATE.IMPORTED_WITH_ERRORS ||
                    data.state === DOC_STATE.IMPORT_INTERRUPTED ||
                    data.state === DOC_STATE.IMPORT_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnCeproImport} />
                    )}
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionSendToDMVS && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(sendToDMVSQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSendToDMVSBtnClick}
                  >
                    {data.state === DOC_STATE.NOTIFYING_CLOSE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnSendToDMVS} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionClose && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionImportInterrupt && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(importInterruptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportInterruptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnInterrupt} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCancel && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(cancelQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCancelBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCancel} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsDestroy && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDelete}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCheckDmvsPendingPozadavky && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkDmvsPendingPozadavkyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckDmvsPendingPozadavkyBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckDmvsPendingPozadavky} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              importInterruptQueryState === QUERY_STATE.WAITING ||
              closeInterruptQueryState === QUERY_STATE.WAITING ||
              cancelQueryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.WAITING ||
              sendToDMVSQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(cancelQueryState === QUERY_STATE.FAIL ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.queryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {(closeInterruptQueryState === QUERY_STATE.FAIL ||
              importInterruptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.interruptQueryStateFail} />
              </DangerAlert>
            )}
            {sendToDMVSQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.sendToDMVSQueryStateFail} />
                {queryMessage?.map((msg, index) => (
                  <React.Fragment key={index}>
                    <br />
                    <ResponseErrorMessage error={msg} />
                  </React.Fragment>
                ))}
              </DangerAlert>
            )}
            {closeQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      <FormRow>
                        <FormItem {...name} isRequired>
                          <Input {...name} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...description}>
                          <Textarea {...description} />
                        </FormItem>
                      </FormRow>
                    </div>
                  </section>
                </div>
              </Form>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.attachments_docs} />
                    </h3>
                    <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                      {uploadHelpText && (
                        <div className='sg-a-mw-fc'>
                          <InfoAlert>{uploadHelpText}</InfoAlert>
                        </div>
                      )}
                      <DocAttachments
                        doc={data}
                        prejimkaType={data.prejimka_type}
                        createDoc={handleSaveDocWithoutValidation}
                        attachments={stateData.attachments}
                        attachmentTypes={CEPRO_ATTACHMENT_TYPES}
                        defaultAttachmentType={ATTACHMENT_TYPES.UPDATE_DATA}
                        importError={importError}
                        importQueryState={importQueryState}
                      />
                    </div>
                  </section>
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDownloadImportLog,
                          link: importLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadDmvsLog,
                          link: dmvsLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {acl.docsActionShowLog && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>
        </article>
      )}
    </MainContent>
  );
};
